import { IEvaluation } from '@/lib/evaluationsApi';
import { IRepository, ICommit } from '@/types';
import useResourceDefinition from '@/hooks/useResourceDefinition';
import { IRemoteWorkspace } from '@/lib/workspaces/workspaceApi';

const getRoundedCost = (cost: number) => {
  const digits = cost >= 1 ? 2 : 4;
  return cost.toFixed(digits);
};

const getApproximateCost = (evaluation: IEvaluation) => {
  const cost = evaluation.credits_used ? parseFloat(evaluation.credits_used) : 0;
  return getRoundedCost(cost);
};

const estimatedRunCost = (evaluation: IEvaluation, totalRows: number) => {
  if (!evaluation.credits_used || !evaluation.progress?.total) return undefined;
  const cost = evaluation.credits_used ? parseFloat(evaluation.credits_used) : 0;
  const costPerRow = cost / evaluation.progress.processed;
  const estimatedTotalCost = costPerRow * totalRows;
  return getRoundedCost(estimatedTotalCost);
};

const getPercentComplete = (evaluation: IEvaluation) =>
  ((evaluation.progress?.processed || 0) / (evaluation.progress?.total || 1)) * 100;

const getSourceUrl = (repository: IRepository, evaluation: IEvaluation) => {
  return (
    '/' +
    [repository.namespace, repository.name, 'file', evaluation.resource.version, evaluation.resource.path].join('/')
  );
};

const hasBranchAdvanced = (evaluation: IEvaluation, repository: IRepository) => {
  const targetBranch = evaluation.conflict_target_branch || evaluation.target_branch;
  const resourcePath = `${targetBranch}/${evaluation.target_path}`;
  const resourceDefinition = useResourceDefinition({
    repository: repository as IRepository,
    filePath: resourcePath.split('/'),
  });
  return resourceDefinition?.branch?.commit_id !== evaluation.closing_commit?.id;
};

const getTargetUrl = (repository: IRepository, evaluation: IEvaluation) => {
  const isBranchAdvanced = hasBranchAdvanced(evaluation, repository);
  const targetBranch = evaluation.conflict_target_branch || evaluation.target_branch;
  const currentVersion = isBranchAdvanced ? evaluation?.closing_commit?.id : targetBranch;
  const commitPathItems = [repository.namespace, repository.name, 'file', currentVersion, evaluation.target_path];
  const workspacePathItems = [
    repository.namespace,
    repository.name,
    'workspaces',
    evaluation.workspace?.id,
    'file',
    evaluation.target_path,
  ];

  if (!!evaluation.closing_commit) {
    return '/' + commitPathItems.join('/');
  }
  if (!!evaluation.workspace?.closing_commit) {
    const workspaceCommitPathItems = [
      repository.namespace,
      repository.name,
      'file',
      evaluation.workspace?.closing_commit?.id,
      evaluation.target_path,
    ];
    return '/' + workspaceCommitPathItems.join('/');
  }
  return '/' + workspacePathItems.join('/');
};

type EvaluationTargetResult = {
  type: 'commit' | 'workspace' | undefined;
  commit?: ICommit;
  workspace?: IRemoteWorkspace;
  url: string;
};

// const getEvaluationCommit = (evaluation: IEvaluation): ICommit | undefined => {
//   return evaluation.closing_commit || undefined || evaluation.workspace?.closing_commit || undefined;
// };

const getEvaluationTarget = (repository: IRepository, evaluation: IEvaluation): EvaluationTargetResult | undefined => {
  const targetUrl = getTargetUrl(repository, evaluation);
  if (evaluation.closing_commit) {
    return { type: 'commit', commit: evaluation.closing_commit, url: targetUrl };
  }
  if (evaluation.workspace?.closing_commit) {
    // console.log('workspace closing commit > workspace', evaluation.workspace);
    return { type: 'commit', commit: evaluation.workspace.closing_commit, url: targetUrl };
  }
  if (evaluation.workspace) {
    return { type: 'workspace', workspace: evaluation.workspace, url: targetUrl };
  }
  return undefined;
};

export {
  estimatedRunCost,
  hasBranchAdvanced,
  getRoundedCost,
  getApproximateCost,
  getPercentComplete,
  getSourceUrl,
  getTargetUrl,
  getEvaluationTarget,
  // getEvaluationCommit,
};
